/**
 * SF-179 Component 5: generic pure aggregation over a per-tick-batch queue (one raw entry per
 * remaining tick/order, not per logical item — same shape as `TrainingQueueAggregator`'s
 * server-side precedent, SF-170). Shared by Training's mobile "In Training" card and
 * Construction's mobile "Under Construction" card, which both previously rendered one row per raw
 * batch un-aggregated (~50 rows for 4 unit types).
 */

export interface AggregateQueueBatchesOptions<T> {
  groupKey: (batch: T) => string;
  ticksRemaining: (batch: T) => number;
  amount: (batch: T) => number;
  /** Ordinal comparator over two groupKeys (e.g. `UnitType`/building-enum ordinal) — output order
   *  must not depend on raw iteration order, which differs between an in-memory `HashMap` and a
   *  post-DB-load `EnumMap` representation (`TrainingQueueAggregator` precedent, SF-170). */
  compare: (a: string, b: string) => number;
}

export interface AggregatedQueueGroup {
  groupKey: string;
  total: number;
  maxTicksRemaining: number;
}

export function aggregateQueueBatches<T>(
  batches: T[],
  { groupKey, ticksRemaining, amount, compare }: AggregateQueueBatchesOptions<T>,
): AggregatedQueueGroup[] {
  const groups = new Map<string, { total: number; maxTicksRemaining: number }>();

  for (const batch of batches) {
    const key = groupKey(batch);
    const existing = groups.get(key) ?? { total: 0, maxTicksRemaining: 0 };
    existing.total += amount(batch);
    // Training's queue is drip-distributed (one raw batch per tick, oldest-completing-first) —
    // min(ticksRemaining) across surviving batches is mathematically pinned to a constant and
    // carries no information (SF-140/SF-170 precedent); max is "days until fully drained." For
    // Construction (per-order, not per-tick), max is the right choice for a different reason:
    // it's the more useful single number for "when is this building type fully done."
    existing.maxTicksRemaining = Math.max(existing.maxTicksRemaining, ticksRemaining(batch));
    groups.set(key, existing);
  }

  const result: AggregatedQueueGroup[] = [];
  for (const [key, { total, maxTicksRemaining }] of groups) {
    // Zero-sum phantom batch group — reachable via fractional training/construction costs, same
    // guard TrainingQueueAggregator already applies (SF-170).
    if (total === 0) continue;
    result.push({ groupKey: key, total, maxTicksRemaining });
  }

  result.sort((a, b) => compare(a.groupKey, b.groupKey));
  return result;
}
